import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, XIcon, TrashIcon, ChartPieIcon, ListIcon,
  ChartBarIcon, BuildingsIcon, CurrencyDollarIcon, ReceiptIcon,
  CheckCircleIcon
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function ExpenseDistribution() {
  const [tab, setTab] = useState("listado");
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [partidas, setPartidas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [banks, setBanks] = useState([]);

  const load = async () => {
    const [l, r, p, s, b] = await Promise.all([
      api.get("/accounting/expense-distributed"),
      api.get("/accounting/expense-distributed/report"),
      api.get("/accounting/expense-accounts"),
      api.get("/suppliers"),
      api.get("/treasury/accounts"),
    ]);
    setItems(l.data); setReport(r.data); setPartidas(p.data);
    setProveedores(s.data); setBanks(b.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <Layout
      title="Gasto Distribuido"
      subtitle={`PRD 4.3 · ${items.length} operaciones · Total ${fmtL(report?.total || 0)}`}
      actions={
        <button
          onClick={() => setOpenForm(true)}
          data-testid="new-gasto-distribuido-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"
        >
          <PlusIcon size={16} weight="bold" /> Nuevo gasto distribuido
        </button>
      }
    >
      <div className="border-b border-border mb-5 flex gap-1 overflow-x-auto">
        {[
          { k: "listado", l: "Listado", i: ListIcon },
          { k: "report", l: "¿Dónde invierto más?", i: ChartPieIcon },
        ].map((t) => (
          <button
            key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-gd-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative flex items-center gap-2 whitespace-nowrap ${
              tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <t.i size={14} weight="duotone" /> {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "listado" && <ListadoView items={items} />}
      {tab === "report" && report && <ReportView report={report} />}

      {openForm && (
        <DistribucionForm
          partidas={partidas}
          proveedores={proveedores}
          banks={banks}
          onClose={() => setOpenForm(false)}
          onSaved={() => { setOpenForm(false); load(); }}
        />
      )}
    </Layout>
  );
}

function ListadoView({ items }) {
  if (!items.length) {
    return (
      <div className="bg-card border border-border/60 rounded-lg p-10 text-center">
        <ReceiptIcon size={32} className="text-text-muted mx-auto mb-3" weight="duotone" />
        <div className="text-text-secondary text-sm">Sin gastos distribuidos registrados</div>
        <div className="text-text-muted text-xs mt-1">Crea uno usando el botón "Nuevo gasto distribuido"</div>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-sidebar">
          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
            <th className="p-3 pl-5">N°</th>
            <th className="p-3">Fecha</th>
            <th className="p-3">Concepto</th>
            <th className="p-3">Líneas</th>
            <th className="p-3 text-right">Total</th>
            <th className="p-3">Método</th>
            <th className="p-3 pr-5">Estado</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`gd-row-${it.numero}`}>
              <td className="p-3 pl-5 font-mono text-xs text-agro-green">{it.numero}</td>
              <td className="p-3 text-xs">{fmtDate(it.fecha)}</td>
              <td className="p-3 text-sm">
                <div>{it.concepto}</div>
                {it.referencia && <div className="text-[10px] text-text-muted font-mono">Ref: {it.referencia}</div>}
              </td>
              <td className="p-3 text-xs text-text-secondary">
                {it.lineas.length} línea{it.lineas.length !== 1 ? "s" : ""}
                <div className="text-[10px] text-text-muted">
                  {it.lineas.slice(0, 2).map((l) => l.partida_nombre || l.proveedor_nombre).join(", ")}
                  {it.lineas.length > 2 && ` +${it.lineas.length - 2}`}
                </div>
              </td>
              <td className="p-3 text-right text-sm font-medium">{fmtL(it.monto_total)}</td>
              <td className="p-3 text-xs text-text-secondary uppercase">{it.metodo}</td>
              <td className="p-3 pr-5">
                <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30">
                  {it.estado}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportView({ report }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="bg-card border border-border/60 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Total invertido</div>
          <div className="font-heading text-3xl font-semibold mt-2 text-agro-green">{fmtL(report.total)}</div>
          <div className="text-xs text-text-muted mt-1">en {report.operaciones} operaciones</div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Partidas y proveedores únicos</div>
          <div className="font-heading text-3xl font-semibold mt-2 text-agro-yellow">
            {report.por_partida.length} <span className="text-base text-text-muted">partidas</span> · {report.por_proveedor.length} <span className="text-base text-text-muted">prov.</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RankCard title="Por partida (cuenta de gasto)" rows={report.por_partida} kind="partida" />
        <RankCard title="Por proveedor" rows={report.por_proveedor} kind="proveedor" />
        {report.por_categoria?.length > 0 && <RankCard title="Por categoría de gasto" rows={report.por_categoria} kind="categoria" />}
        {report.por_linea?.length > 0 && <RankCard title="Por línea de producto" rows={report.por_linea} kind="linea" />}
        {report.por_fuente?.length > 0 && <RankCard title="Por origen (distribuido · gastos · viáticos)" rows={report.por_fuente} kind="fuente" />}
      </div>
    </div>
  );
}

function RankCard({ title, rows, kind }) {
  const Icon = kind === "partida" ? ChartBarIcon : BuildingsIcon;
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border/60 flex items-center gap-3">
        <Icon size={16} className="text-agro-green" weight="duotone" />
        <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-text-muted text-sm">Sin datos en este período</div>
      ) : (
        <table className="w-full">
          <tbody>
            {rows.map((r, idx) => {
              const max = rows[0].porcentaje || 1;
              const barWidth = (r.porcentaje / max) * 100;
              return (
                <tr key={(r.codigo || r.id) + idx} className="border-b border-border/30" data-testid={`rank-${kind}-${idx}`}>
                  <td className="p-3 pl-5 w-8 text-xs font-mono text-text-muted">#{idx + 1}</td>
                  <td className="p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-text-primary">{r.nombre || r.codigo || r.id}</div>
                        <div className="text-[10px] text-text-muted mt-0.5">{r.ops} operaci{r.ops === 1 ? "ón" : "ones"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-medium text-agro-green">{fmtL(r.monto)}</div>
                        <div className="text-[10px] text-text-muted">{r.porcentaje}%</div>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 bg-app rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-agro-green to-agro-yellow rounded-full" style={{ width: `${barWidth}%` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DistribucionForm({ partidas, proveedores, banks, onClose, onSaved }) {
  const [categorias, setCategorias] = useState([]);
  const [lineasProd, setLineasProd] = useState([]);
  useEffect(() => {
    api.get("/expenses/categories").then((r) => setCategorias(r.data || [])).catch(() => {});
    api.get("/products/categories").then((r) => setLineasProd((r.data || []).map((x) => (typeof x === "string" ? x : x.nombre || x.categoria || "")).filter(Boolean))).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    concepto: "", referencia: "", monto_total: 0,
    metodo: "transferencia", cuenta_banco_id: "", fecha: "",
  });
  const [lineas, setLineas] = useState([
    { partida_codigo: "", proveedor_id: "", porcentaje: 100, descripcion: "" },
  ]);

  const totalPct = useMemo(() => lineas.reduce((s, l) => s + Number(l.porcentaje || 0), 0), [lineas]);
  const cuadrado = Math.abs(totalPct - 100) < 0.01;

  const updateLine = (i, patch) => {
    const next = [...lineas]; next[i] = { ...next[i], ...patch }; setLineas(next);
  };
  const addLine = () => setLineas([...lineas, { partida_codigo: "", proveedor_id: "", porcentaje: 0, descripcion: "" }]);
  const removeLine = (i) => setLineas(lineas.filter((_, idx) => idx !== i));
  const distribuirIgual = () => {
    const pct = Math.round((100 / lineas.length) * 100) / 100;
    setLineas(lineas.map((l, i) => ({ ...l, porcentaje: i === lineas.length - 1 ? +(100 - pct * (lineas.length - 1)).toFixed(2) : pct })));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cuadrado) return toast.error(`La suma de % debe ser 100 (actual ${totalPct.toFixed(2)})`);
    if (Number(form.monto_total) <= 0) return toast.error("Monto total debe ser > 0");
    for (const l of lineas) {
      if (!l.partida_codigo && !l.proveedor_id && !l.linea && !l.categoria_id) return toast.error("Cada línea requiere partida, proveedor/línea o categoría");
    }
    try {
      await api.post("/accounting/expense-distributed", {
        concepto: form.concepto, referencia: form.referencia,
        monto_total: Number(form.monto_total), metodo: form.metodo,
        cuenta_banco_id: form.cuenta_banco_id || null,
        fecha: form.fecha ? new Date(form.fecha).toISOString() : undefined,
        lineas: lineas.map((l) => ({
          partida_codigo: l.partida_codigo || null,
          proveedor_id: l.proveedor_id || null,
          linea: l.linea || "",
          categoria_id: l.categoria_id || null,
          porcentaje: Number(l.porcentaje),
          descripcion: l.descripcion,
        })),
      });
      toast.success("Gasto distribuido registrado. Asiento contable generado.");
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: { form, lineas }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gd-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.3 · Gasto distribuido</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">Nuevo gasto con distribución</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>

        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Concepto *</label>
              <input
                data-testid="gd-concepto"
                value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                placeholder="ej. Planilla quincenal mixta"
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Referencia</label>
              <input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Monto total (L.) *</label>
              <input
                data-testid="gd-monto-total"
                type="number" step="0.01" min="0"
                value={form.monto_total} onChange={(e) => setForm({ ...form, monto_total: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Método</label>
              <select value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="deposito">Depósito</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cuenta tesorería</label>
              <select value={form.cuenta_banco_id} onChange={(e) => setForm({ ...form, cuenta_banco_id: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                <option value="">Automática</option>
                {banks.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre} ({fmtL(b.saldo_actual || 0)})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Fecha</label>
              <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
          </div>

          {/* Líneas */}
          <div className="border border-border rounded-md overflow-hidden">
            <div className="bg-sidebar p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ChartPieIcon size={14} className="text-agro-green" weight="duotone" />
                <span className="text-[10px] uppercase tracking-wider text-text-muted font-medium">Líneas de distribución</span>
              </div>
              <button type="button" onClick={distribuirIgual} className="text-xs text-agro-yellow hover:underline">
                Distribuir igual
              </button>
            </div>
            <table className="w-full">
              <thead className="bg-sidebar/40">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2 pl-4">Partida (cuenta de gasto)</th>
                  <th className="p-2">Proveedor / línea / categoría</th>
                  <th className="p-2 text-right w-24">%</th>
                  <th className="p-2 text-right w-32">Monto</th>
                  <th className="p-2">Descripción</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((l, i) => {
                  const monto = Number(form.monto_total || 0) * (Number(l.porcentaje || 0) / 100);
                  return (
                    <tr key={i} className="border-t border-border/40">
                      <td className="p-2 pl-4">
                        <select
                          data-testid={`gd-line-partida-${i}`}
                          value={l.partida_codigo}
                          onChange={(e) => updateLine(i, { partida_codigo: e.target.value })}
                          className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs"
                        >
                          <option value="">— partida —</option>
                          {partidas.map((p) => <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre}</option>)}
                        </select>
                      </td>
                      <td className="p-2">
                        <select
                          value={l.proveedor_id}
                          onChange={(e) => updateLine(i, { proveedor_id: e.target.value })}
                          className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs"
                        >
                          <option value="">— sin proveedor —</option>
                          {proveedores.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                        </select>
                        <div className="grid grid-cols-2 gap-1 mt-1">
                          <select value={l.linea || ""} onChange={(e) => updateLine(i, { linea: e.target.value })} data-testid={`gd-line-linea-${i}`}
                            className="w-full bg-app border border-border rounded px-1 py-1 text-[11px]">
                            <option value="">— línea —</option>
                            {lineasProd.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                          <select value={l.categoria_id || ""} onChange={(e) => updateLine(i, { categoria_id: e.target.value })} data-testid={`gd-line-cat-${i}`}
                            className="w-full bg-app border border-border rounded px-1 py-1 text-[11px]">
                            <option value="">— categoría —</option>
                            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                          </select>
                        </div>
                      </td>
                      <td className="p-2">
                        <input
                          data-testid={`gd-line-pct-${i}`}
                          type="number" step="0.01" min="0" max="100"
                          value={l.porcentaje}
                          onChange={(e) => updateLine(i, { porcentaje: e.target.value })}
                          className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right"
                        />
                      </td>
                      <td className="p-2 text-right text-xs text-agro-green font-medium">{fmtL(monto)}</td>
                      <td className="p-2">
                        <input value={l.descripcion} onChange={(e) => updateLine(i, { descripcion: e.target.value })}
                          placeholder="Opcional"
                          className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs" />
                      </td>
                      <td className="p-2">
                        {lineas.length > 1 && (
                          <button aria-label="Eliminar" type="button" onClick={() => removeLine(i)} className="text-text-muted hover:text-red-400">
                            <TrashIcon size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-sidebar/40 border-t border-border">
                  <td colSpan="2" className="p-2 pl-4">
                    <button type="button" onClick={addLine} data-testid="gd-add-line" className="text-agro-green text-xs hover:underline flex items-center gap-1">
                      <PlusIcon size={12} weight="bold" /> Añadir línea / proveedor
                    </button>
                  </td>
                  <td className={`p-2 text-right text-sm font-medium ${cuadrado ? "text-agro-green" : "text-agro-yellow"}`}>{totalPct.toFixed(2)}%</td>
                  <td className="p-2 text-right text-sm font-medium text-agro-green">{fmtL(form.monto_total || 0)}</td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className={`p-3 rounded-md text-sm flex items-center justify-between ${cuadrado ? "bg-agro-green/10 border border-agro-green/30 text-agro-green" : "bg-agro-yellow/10 border border-agro-yellow/30 text-agro-yellow"}`}>
            <span>{cuadrado ? <><CheckCircleIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Suma 100% — listo para registrar</> : `La suma debe ser 100% (faltan ${(100 - totalPct).toFixed(2)}%)`}</span>
            <CurrencyDollarIcon size={16} weight="duotone" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button
              type="submit"
              disabled={(!cuadrado) || m.saving}
              data-testid="gd-submit-btn"
              className="flex-1 bg-agro-green hover:bg-agro-green-hover disabled:opacity-50 text-black font-semibold rounded-md py-2.5 text-sm"
            >
              Registrar gasto distribuido
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
